/**
 * WebSocket connection state management.
 *
 * Tracks connection status, client identity, latency,
 * and reconnection state. Uses Zustand with immer.
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type { ConnectionStatus, ClientRole } from '@assistmd/types';

export interface WsState {
  status: ConnectionStatus;
  clientId: string | null;
  role: ClientRole | null;
  serverVersion: string | null;
  reconnectAttempt: number;
  latencyMs: number | null;
  lastError: string | null;
  lastConnectedAt: number | null;
}

interface WsActions {
  setConnecting: () => void;
  setConnected: (clientId: string, role: ClientRole, serverVersion: string) => void;
  setDisconnected: (error?: string) => void;
  setReconnecting: (attempt: number) => void;
  setLatency: (ms: number) => void;
  reset: () => void;
}

type WsStore = WsState & WsActions;

const initialState: WsState = {
  status: 'disconnected',
  clientId: null,
  role: null,
  serverVersion: null,
  reconnectAttempt: 0,
  latencyMs: null,
  lastError: null,
  lastConnectedAt: null,
};

export const useWsStore = create<WsStore>()(
  immer((set) => ({
    ...initialState,

    setConnecting: () => {
      set((state) => {
        state.status = 'connecting';
        state.lastError = null;
      });
    },

    setConnected: (clientId, role, serverVersion) => {
      set((state) => {
        state.status = 'connected';
        state.clientId = clientId;
        state.role = role;
        state.serverVersion = serverVersion;
        state.reconnectAttempt = 0;
        state.lastError = null;
        state.lastConnectedAt = Date.now();
      });
    },

    setDisconnected: (error) => {
      set((state) => {
        state.status = 'disconnected';
        state.lastError = error ?? null;
      });
    },

    setReconnecting: (attempt) => {
      set((state) => {
        state.status = 'connecting';
        state.reconnectAttempt = attempt;
      });
    },

    setLatency: (ms) => {
      set((state) => {
        state.latencyMs = ms;
      });
    },

    reset: () => {
      set(() => ({ ...initialState }));
    },
  })),
);
